// OB-21 — pure display logic for the portal credit dashboard, kept out of JSX so the honesty
// rules (docs/specs/obligo/19-ob-21-portal-credit-dashboard.md §3.3) are one small, readable
// module rather than scattered through markup. Nothing here calls the network or touches state.

import { creditLineUtilizationPctToken } from '@arbitrip/constants';

const MONEY_FRACTION_DIGITS = 2;
const MONTHS_IN_YEAR = 12;

/**
 * #61 — the ONE money formatter on this page, for DISPLAY only: comparisons and states keep reading
 * the server's unrounded figures. At most two decimals; a whole amount keeps the page's "700" rather
 * than "700.00", and a fractional one always carries both decimals so "12.30" never reads as "12.3".
 */
export function formatMoneyAmount(amount) {
	if (!Number.isFinite(amount)) {
		return '';
	}

	const rounded = Number(amount.toFixed(MONEY_FRACTION_DIGITS)) || 0;
	const fraction_digits = Number.isInteger(rounded) ? 0 : MONEY_FRACTION_DIGITS;

	return rounded.toLocaleString(undefined, {
		minimumFractionDigits: fraction_digits,
		maximumFractionDigits: fraction_digits,
	});
}

/**
 * §3.3 — the percentage a customer reads, through the ONE rule every credit-line surface shares
 * (`@arbitrip/constants` `creditLineUtilizationPctToken`; see its banner for BUG-44). It FLOORS: a
 * month at 89.78% of a cap whose block threshold is 90 used to print "90%" here and in the trader
 * screen, the Slack post and the alert email alike, which reads as a month that has hit the line
 * when it has not.
 *
 * The TOKEN and not the formatted string: this portal's i18n entries already carry the sign
 * (`"{{utilization_pct}}% used"`), so interpolating `89%` would render `89%%`.
 */
export function utilizationPctToken(utilization_pct) {
	return creditLineUtilizationPctToken(utilization_pct);
}

export const CELL_KIND = {
	UNKNOWN: 'unknown', // §3.3 — never 0, never 0%, never an empty bar
	UNRECONSTRUCTIBLE: 'unreconstructible', // §2.2a (M13) — `used` shown, denominator withheld
	UNCAPPED: 'uncapped', // a NIL cap — uncapped, which is the opposite of CLOSED's `0`
	PAST_DUE: 'past_due', // §3.4 — an elapsed month with a non-zero bucket; its headroom is not capacity
	CLOSED: 'closed', // a `0` override — never "no limit"
	ON_HOLD: 'on_hold', // the ROW is blocked manually (OB-15) — every month is refused, whatever its `state`
	BLOCK: 'block', // `state: 'block'` — what it MEANS differs by `mode`, see `blockCopyKey`
	ALERT: 'alert', // `state: 'alert'`
	NORMAL: 'normal', // `state: 'ok'`, or a cap that reports no state at all
};

// Presentation tone, not colour: the caller owns the palette and the icon. `closed` and `unknown`
// are deliberately NOT `block` — "we cannot tell you a number" and "finance shut this month" are
// different claims from "this month is used up", and only the last is a threshold event.
export const CELL_TONE = {
	OK: 'ok',
	ALERT: 'alert',
	BLOCK: 'block',
	CLOSED: 'closed',
	UNKNOWN: 'unknown',
};

/**
 * Which of the honesty states a cell is in, and nothing about presentation. Order matters:
 * `status: 'unknown'` at the ROW level (no cap at all for this month's cell) is checked by the
 * caller before this — this function is about ONE cell that DOES exist in `by_month`.
 *
 * `row` carries the ROW-level facts a cell cannot see. `blocked_manually` is one of them: the gate
 * (packages/credit-line/credit_line_availability.ts) reads it BEFORE the mode dial and refuses
 * EVERY month in BOTH modes, so a held line must never render a month as having bookable headroom.
 */
export function classifyCell(cell, row) {
	if (!cell) {
		return CELL_KIND.UNKNOWN;
	}

	// The honesty states come FIRST, and in this order. Each means "the figure you would read off
	// this cell is not the figure you think it is", which outranks `state` — `state` answers "how
	// full is this month", a question that only has an answer once the numbers are safe to show.
	if (cell.cap_basis === 'unreconstructible') {
		return CELL_KIND.UNRECONSTRUCTIBLE;
	}

	if (isPastDueUnsettled(cell)) {
		// §3.4.1 — an elapsed month with a non-zero bucket NEVER renders bookable headroom, whatever
		// its `state` says: "19 left" on January in March is a lie the customer will plan against.
		return CELL_KIND.PAST_DUE;
	}

	if (cell.closed) {
		// `cap === 0`, which `monthState` also reports as `block`. CLOSED wins because it says WHY:
		// finance shut the month, rather than the customer having filled it.
		return CELL_KIND.CLOSED;
	}

	if (isNil(cell.cap)) {
		// No cap at any level of the chain (`monthState`, packages/credit-line/credit_line.js) — there is
		// no denominator, so there is no percentage and no "left", and none may be implied.
		return CELL_KIND.UNCAPPED;
	}

	if (row && row.blocked_manually) {
		// A manual hold binds in both modes and for every month, so no month on this row may render a
		// "left" figure or a bar. It sits BELOW the honesty states because those say the numbers
		// themselves cannot be read off the cell, which is the stronger claim.
		return CELL_KIND.ON_HOLD;
	}

	if (cell.state === 'block') {
		return CELL_KIND.BLOCK;
	}

	if (cell.state === 'alert') {
		return CELL_KIND.ALERT;
	}

	return CELL_KIND.NORMAL;
}

function isNil(value) {
	return value === null || value === undefined;
}

// Is anything on this row actually being refused right now? `blocked_manually` binds in both modes
// (OB-15), and only `enforce` turns a computed threshold into a refusal.
function isEnforcing(row) {
	return Boolean(row && (row.blocked_manually || row.mode === 'enforce'));
}

/**
 * The word and the tone one cell carries. Split from `cellCopy` because a cell needs a word AND a
 * sentence, and only the word fits on a card.
 *
 * The REFUSAL tone (the caller paints it red, with a block glyph) is reserved for months where
 * something is genuinely being declined: an enforced block, or a manual hold in either mode. Colour
 * and glyph are read before the sentence beneath them, so a shadow-mode block — where nothing is
 * refused — gets the attention tone instead. In shadow, block and alert differ in degree, not in
 * consequence, and looking alike is the honest outcome.
 */
export function cellBadge(cell, row) {
	const kind = classifyCell(cell, row);

	switch (kind) {
		case CELL_KIND.ON_HOLD:
			return { kind, key: 'CreditLinePage.StateOnHold', tone: CELL_TONE.BLOCK };
		case CELL_KIND.UNKNOWN:
			return { kind, key: 'CreditLinePage.StateNotMeasured', tone: CELL_TONE.UNKNOWN };
		case CELL_KIND.UNRECONSTRUCTIBLE:
			return { kind, key: 'CreditLinePage.StateLimitUnavailable', tone: CELL_TONE.UNKNOWN };
		case CELL_KIND.UNCAPPED:
			return { kind, key: 'CreditLinePage.StateNoLimitSet', tone: CELL_TONE.UNKNOWN };
		case CELL_KIND.PAST_DUE:
			return { kind, key: 'CreditLinePage.StateMonthEnded', tone: CELL_TONE.CLOSED };
		case CELL_KIND.CLOSED:
			return { kind, key: 'CreditLinePage.StateClosed', tone: CELL_TONE.CLOSED };
		case CELL_KIND.BLOCK:
			return {
				kind,
				key: 'CreditLinePage.StateBlocked',
				tone: isEnforcing(row) ? CELL_TONE.BLOCK : CELL_TONE.ALERT,
			};
		case CELL_KIND.ALERT:
			return { kind, key: 'CreditLinePage.StateNearingLimit', tone: CELL_TONE.ALERT };
		default:
			return { kind, key: 'CreditLinePage.StateWithinLimit', tone: CELL_TONE.OK };
	}
}

/**
 * §4 — what a `state: 'block'` month MEANS, which is a property of the ROW's mode, not of the cell.
 * In `shadow` NOTHING is refused: the cap is being measured, so a blocked month must never tell the
 * customer their bookings are being declined. `blocked_manually` (OB-15) binds in BOTH modes and so
 * never carries the shadow wording — the same trap `modeBadge` guards below.
 */
export function blockCopyKey({ mode, blocked_manually } = {}) {
	if (blocked_manually) {
		return 'CreditLinePage.BlockedOnHold';
	}

	if (mode === 'enforce') {
		return 'CreditLinePage.BlockedEnforce';
	}

	return 'CreditLinePage.BlockedShadow';
}

/**
 * What a CLOSED month (`cap === 0`) MEANS, which — exactly like `blockCopyKey` — is a property of the
 * ROW's mode and not of the cell. A closed month is NOT "fully used": nothing was consumed, finance
 * shut it, so it must never borrow the block copy. And in `shadow` it refuses nothing, so it must not
 * claim it is closed to bookings while the hero beside it says nothing is being declined.
 */
export function closedCopyKey({ mode, blocked_manually } = {}) {
	if (blocked_manually) {
		return 'CreditLinePage.BlockedOnHold';
	}

	if (mode === 'enforce') {
		return 'CreditLinePage.ClosedEnforce';
	}

	return 'CreditLinePage.ClosedShadow';
}

/**
 * §3.3 — an ALLOWLIST of the kinds whose cap/used/headroom figures may be shown, never a denylist of
 * the ones that may not: a new honesty state added later must fail CLOSED (no figures) rather than
 * silently start rendering numbers it has no right to.
 */
export function mayShowFigures(cell, row) {
	const kind = classifyCell(cell, row);

	return kind === CELL_KIND.NORMAL || kind === CELL_KIND.ALERT || kind === CELL_KIND.BLOCK;
}

/**
 * §3.3 — the "left this month" figure, or null where there is none to show. `headroom` is NOT clamped
 * server-side: a month used past its cap reports a NEGATIVE headroom, and "-5,000 left" is not a
 * quantity anyone can plan against. Over-cap gets its own label instead, so the number stays true and
 * the word changes. The bar reading 100% while the text reads 150% used is deliberate and honest.
 */
export function headroomFigure(cell) {
	const headroom = cell && cell.headroom;

	if (!Number.isFinite(headroom)) {
		return null;
	}

	if (headroom < 0) {
		return { labelKey: 'CreditLinePage.HeroOver', amount: Math.abs(headroom) };
	}

	return { labelKey: 'CreditLinePage.HeroLeft', amount: headroom };
}

/**
 * §4's body copy — the sentence beside the mode badge. Same three-way split for the same reason: a
 * badge is one word, and one word cannot carry "nothing is being declined because of it".
 */
export function modeBodyKey({ mode, blocked_manually } = {}) {
	if (blocked_manually) {
		return 'CreditLinePage.OnHoldBody';
	}

	if (mode === 'enforce') {
		return 'CreditLinePage.ModeActiveBody';
	}

	return 'CreditLinePage.ModeMonitoringBody';
}

/**
 * Whether a cell's reservation list is worth opening. An UNKNOWN month has nothing behind it; a
 * measured month does even at zero, because "no reservations are holding this month" is an answer.
 */
export function isDrillable(cell, row) {
	const kind = classifyCell(cell, row);

	if (kind === CELL_KIND.UNKNOWN) {
		return false;
	}

	return cell.used > 0 || kind !== CELL_KIND.UNRECONSTRUCTIBLE;
}

/**
 * §3.3 — the bar reads `utilization_pct` OFF THE SERVER and never recomputes it from used/cap, so
 * the gate and the screen cannot disagree. Returns null wherever a bar would be a claim we cannot
 * back: no percentage at all, or a kind whose denominator is withheld or whose headroom is not
 * capacity. A null here means "render no bar" — never an empty one (§3.3).
 */
export function utilizationBarValue(cell, row) {
	const kind = classifyCell(cell, row);
	const withheld = [
		CELL_KIND.UNKNOWN,
		CELL_KIND.UNRECONSTRUCTIBLE,
		CELL_KIND.UNCAPPED,
		CELL_KIND.PAST_DUE,
		CELL_KIND.ON_HOLD,
	];

	if (withheld.includes(kind) || !Number.isFinite(cell.utilization_pct)) {
		return null;
	}

	return Math.max(0, Math.min(100, cell.utilization_pct));
}

/**
 * The i18n key + interpolation values for one cell, keyed by `classifyCell`'s answer. The caller
 * still owns `t(...)` — this only decides WHICH key and WHAT numbers, never renders a string
 * itself, so the same logic serves both the month-strip cell and any summary text.
 */
export function cellCopy(cell, row) {
	const kind = classifyCell(cell, row);

	switch (kind) {
		case CELL_KIND.UNKNOWN:
			return { kind, key: 'CreditLinePage.NotMeasuredYet' };
		case CELL_KIND.UNRECONSTRUCTIBLE:
			// An unreconstructible cell is past-due by construction, and `classifyCell`'s precedence —
			// correctly — never lets it reach the PAST_DUE branch. Both facts are true and the customer
			// needs both, so the copy carries them together rather than losing the larger one.
			return {
				kind,
				key: isPastDueUnsettled(cell)
					? 'CreditLinePage.UnreconstructiblePastDue'
					: 'CreditLinePage.UnreconstructibleMonth',
				values: { used: formatMoneyAmount(cell.used) },
			};
		case CELL_KIND.UNCAPPED:
			return { kind, key: 'CreditLinePage.NoMonthlyLimit', values: { used: formatMoneyAmount(cell.used) } };
		case CELL_KIND.ON_HOLD:
			return { kind, key: 'CreditLinePage.OnHoldMonth', values: { used: formatMoneyAmount(cell.used) } };
		case CELL_KIND.PAST_DUE:
			// §3.4.3 — factual, never a debt notice: the credit is consumed, not overdue, and OB-19's
			// settlement mechanics are deferred and must not be implied here.
			return { kind, key: 'CreditLinePage.PastDueUnsettled', values: { used: formatMoneyAmount(cell.used) } };
		case CELL_KIND.CLOSED:
			// The FACT only — a zero limit, true in both modes. What it COSTS the customer depends on the
			// row's mode and is `closedCopyKey`'s sentence, never this one.
			return { kind, key: 'CreditLinePage.ClosedZeroLimit', values: { used: formatMoneyAmount(cell.used) } };
		default:
			return {
				kind,
				key: cell.headroom < 0 ? 'CreditLinePage.CellSummaryOver' : 'CreditLinePage.CellSummary',
				values: {
					used: formatMoneyAmount(cell.used),
					cap: formatMoneyAmount(cell.cap),
					headroom: formatMoneyAmount(Math.max(0, cell.headroom)),
					over: formatMoneyAmount(Math.abs(Math.min(0, cell.headroom))),
					utilization_pct: utilizationPctToken(cell.utilization_pct),
				},
			};
	}
}

/**
 * §2.3 — a month is past-due-unsettled when it has ended and its bucket is non-zero. It is never
 * a threshold event and never renders bookable headroom (§3.4) — this function is the one place
 * that decides "does this cell get the closed-with-debt treatment", so the strip and any summary
 * agree with each other by construction.
 */
export function isPastDueUnsettled(cell) {
	return Boolean(cell) && cell.past_due_unsettled > 0;
}

/**
 * §4 — the mode badge. `mode` is whatever the server actually reports; this NEVER asserts
 * enforcement is live beyond what `mode` says, and a manual block overrides the mode's own
 * wording because it binds in both modes (OB-15) and must never carry the shadow disclaimer.
 */
export function modeBadge({ mode, blocked_manually }) {
	if (blocked_manually) {
		return { key: 'CreditLinePage.OnHold', tone: 'hold' };
	}

	if (mode === 'enforce') {
		return { key: 'CreditLinePage.ModeActive', tone: 'active' };
	}

	return { key: 'CreditLinePage.ModeMonitoring', tone: 'monitoring' };
}

/**
 * §3.5 — a shared line reports the FACT with no attribution. `row.shared` is the only signal the
 * payload carries (§5's allowlist drops holder identity entirely); this returns null when there
 * is nothing to say, so a caller can render conditionally without re-deriving the rule.
 */
export function sharedBannerKey(row) {
	return row && row.shared ? 'CreditLinePage.SharedBanner' : null;
}

/**
 * §3.6's drill-down remainder line. `unattributed` is `null` on an unshared line (§5) — never a
 * silent zero — so the caller must check for null before rendering, and this function is the one
 * place that decides whether there is a line to render at all.
 */
export function hasUnattributedRemainder(drilldown) {
	return Boolean(drilldown && drilldown.unattributed);
}

/**
 * The `YYYY-MM` bucket key for an instant. The ONE place this page decides what "this month" is, so
 * the hero and the strip cannot land on different months.
 *
 * Callers pass the instant the SERVER reported (`as_of`), never a clock of their own: the bucket a
 * booking charges is the server's, and a client that picks its own month can label a bucket "this
 * month" that the gate is not charging. The fields are read in UTC because that is exactly how the
 * server buckets (`currentBucketMonth`, packages/credit-line/credit_line_alerts.js) — reading the
 * server's own instant with local fields would put the disagreement straight back.
 */
export function bucketMonthKey(at) {
	const date = at instanceof Date ? at : new Date(at);

	return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

function shiftBucketMonth(month, delta) {
	const [year, month_number] = month.split('-').map(Number);

	return bucketMonthKey(new Date(Date.UTC(year, month_number - 1 + delta, 1)));
}

/**
 * #56 — the strip slides over ONE year only: this month through the same month a year later. The
 * server's axis may reach further either way (past-due months behind, `FORWARD_MONTHS` ahead); this
 * narrows what the strip offers and never invents a month the server did not return.
 */
export function monthStripRange(months, now = new Date()) {
	if (!Array.isArray(months)) {
		return [];
	}

	const first = bucketMonthKey(now);
	const last = shiftBucketMonth(first, MONTHS_IN_YEAR);

	return months.filter((month) => month >= first && month <= last);
}

/**
 * §3.2 — the month strip's default position. `months` is the FULL axis the server already
 * returned (bounded backward through non-zero buckets, `FORWARD_MONTHS` ahead — never re-derived
 * or re-requested here); this only decides which slice of it to show first. Defaults to the
 * current month leading, with one prior month visible where the data has one, so a reader lands
 * on "now" rather than on whichever end of the array happens to sort first.
 */
export function defaultMonthWindowStart(months, windowSize, now = new Date()) {
	if (!Array.isArray(months) || months.length === 0) {
		return 0;
	}

	const current_index = months.indexOf(bucketMonthKey(now));

	if (current_index < 0) {
		// No exact match (e.g. the axis is entirely past-due-unsettled months) — start at the end,
		// the closest thing to "now" the data actually has.
		return Math.max(0, months.length - windowSize);
	}

	return Math.max(0, Math.min(current_index - 1, months.length - windowSize));
}
